import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  RefreshCw,
  Unplug,
  Plug,
  CheckCircle,
  AlertCircle,
  Clock,
  Plus,
  Pencil,
  Trash2,
  Settings2,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/app/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import {
  useQuickBooksStatus,
  useQuickBooksConnect,
  useQuickBooksDisconnect,
  useQuickBooksSync,
  useQBAccountMappings,
  useCreateQBAccountMapping,
  useUpdateQBAccountMapping,
  useDeleteQBAccountMapping,
} from '@/hooks/use-payroll';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { formatDateTimeInTimeZone } from '@/lib/date-utils';
import type {
  QuickBooksSyncPayload,
  QBMappingType,
  QuickBooksAccountMapping,
} from '@/lib/types';

const MAPPING_TYPE_OPTIONS: { value: QBMappingType; label: string; hasSource: boolean }[] = [
  { value: 'expense_category_to_account', label: 'Expense Category \u2192 QB Account', hasSource: true },
  { value: 'cost_code_to_account', label: 'Cost Code \u2192 QB Account', hasSource: true },
  { value: 'client_to_customer', label: 'Client/Project \u2192 QB Customer', hasSource: true },
  { value: 'payroll_expense_account', label: 'Payroll Expense Account', hasSource: false },
  { value: 'payroll_payable_account', label: 'Payroll Payable Account', hasSource: false },
  { value: 'payroll_reimbursement_bank_account', label: 'Payroll Reimbursement Bank Account', hasSource: false },
  { value: 'payroll_per_diem_account', label: 'Payroll Per Diem Account', hasSource: false },
  { value: 'payroll_mileage_account', label: 'Payroll Mileage Account', hasSource: false },
  { value: 'payroll_expense_reimbursement_account', label: 'Payroll Expense Reimbursement Account', hasSource: false },
  { value: 'default_service_item', label: 'Default Service Item', hasSource: false },
];

const MAPPING_TYPE_GROUPS: { group: string; types: QBMappingType[] }[] = [
  {
    group: 'Expense Mappings',
    types: ['expense_category_to_account', 'cost_code_to_account'],
  },
  {
    group: 'Invoice Mappings',
    types: ['client_to_customer', 'default_service_item'],
  },
  {
    group: 'Payroll Mappings',
    types: [
      'payroll_expense_account',
      'payroll_payable_account',
      'payroll_reimbursement_bank_account',
      'payroll_per_diem_account',
      'payroll_mileage_account',
      'payroll_expense_reimbursement_account',
    ],
  },
];

interface MappingFormState {
  mapping_type: QBMappingType;
  source_id: string;
  qb_ref_value: string;
  qb_ref_name: string;
}

const EMPTY_FORM: MappingFormState = {
  mapping_type: 'expense_category_to_account',
  source_id: '',
  qb_ref_value: '',
  qb_ref_name: '',
};

export function QuickBooksSettingsPage() {
  const timeZone = useOrgTimeZone();
  const { data: status, isLoading } = useQuickBooksStatus();
  const connect = useQuickBooksConnect();
  const disconnect = useQuickBooksDisconnect();
  const sync = useQuickBooksSync();
  const { data: mappings = [], isLoading: mappingsLoading } = useQBAccountMappings();
  const createMapping = useCreateQBAccountMapping();
  const updateMapping = useUpdateQBAccountMapping();
  const deleteMapping = useDeleteQBAccountMapping();

  const [syncType, setSyncType] = useState<QuickBooksSyncPayload['sync_type']>('expenses');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [syncResult, setSyncResult] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMapping, setEditingMapping] = useState<QuickBooksAccountMapping | null>(null);
  const [form, setForm] = useState<MappingFormState>(EMPTY_FORM);

  const handleConnect = async () => {
    try {
      const result = await connect.mutateAsync();
      window.location.href = result.authorization_url;
    } catch {
      // Error handled by mutation
    }
  };

  const handleDisconnect = () => {
    disconnect.mutate();
  };

  const handleSync = async () => {
    setSyncResult(null);
    try {
      const result = await sync.mutateAsync({
        sync_type: syncType,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      });
      if (result.error) {
        setSyncResult(`Error: ${result.error}`);
      } else {
        setSyncResult(
          `Synced: ${result.synced ?? 0}, Failed: ${result.failed ?? 0}, Skipped: ${result.skipped ?? 0}`
        );
      }
    } catch {
      setSyncResult('Sync failed. Please try again.');
    }
  };

  const openCreate = () => {
    setEditingMapping(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (m: QuickBooksAccountMapping) => {
    setEditingMapping(m);
    setForm({
      mapping_type: m.mapping_type,
      source_id: m.source_id != null ? String(m.source_id) : '',
      qb_ref_value: m.qb_ref_value,
      qb_ref_name: m.qb_ref_name,
    });
    setDialogOpen(true);
  };

  const handleDelete = (id: number) => {
    deleteMapping.mutate(id);
  };

  const handleSave = async () => {
    const selectedType = MAPPING_TYPE_OPTIONS.find((o) => o.value === form.mapping_type);
    const payload = {
      mapping_type: form.mapping_type,
      source_id: selectedType?.hasSource && form.source_id ? Number(form.source_id) : null,
      qb_ref_value: form.qb_ref_value,
      qb_ref_name: form.qb_ref_name,
    };

    try {
      if (editingMapping) {
        await updateMapping.mutateAsync({ id: editingMapping.id, ...payload });
      } else {
        await createMapping.mutateAsync(payload);
      }
      setDialogOpen(false);
    } catch {
      // Error handled by mutation
    }
  };

  const selectedTypeOption = MAPPING_TYPE_OPTIONS.find((o) => o.value === form.mapping_type);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" asChild>
          <Link to="/payroll">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">QuickBooks Online</h1>
          <p className="text-gray-600 mt-1">Connect and sync with QuickBooks Online</p>
        </div>
      </div>

      {/* Connection Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {status?.connected ? (
              <CheckCircle className="h-5 w-5 text-green-500" />
            ) : (
              <AlertCircle className="h-5 w-5 text-gray-400" />
            )}
            Connection Status
          </CardTitle>
          <CardDescription>
            {isLoading
              ? 'Checking connection...'
              : status?.connected
                ? 'Your QuickBooks Online account is connected'
                : 'Not connected to QuickBooks Online'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {status?.connected ? (
            <>
              <div className="flex flex-wrap gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Realm ID:</span>{' '}
                  <span className="font-mono">{status.realm_id}</span>
                </div>
                <div>
                  <span className="text-gray-500">Status:</span>{' '}
                  <Badge variant={status.is_active ? 'default' : 'secondary'}>
                    {status.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                {status.is_token_expired && (
                  <Badge variant="destructive">Token Expired</Badge>
                )}
                {status.last_synced && (
                  <div>
                    <span className="text-gray-500">Last Synced:</span>{' '}
                    {formatDateTimeInTimeZone(status.last_synced, timeZone)}
                  </div>
                )}
              </div>
              <Button
                variant="destructive"
                onClick={handleDisconnect}
                disabled={disconnect.isPending}
              >
                <Unplug className="h-4 w-4 mr-2" />
                {disconnect.isPending ? 'Disconnecting...' : 'Disconnect'}
              </Button>
            </>
          ) : (
            <Button onClick={handleConnect} disabled={connect.isPending}>
              <Plug className="h-4 w-4 mr-2" />
              {connect.isPending ? 'Connecting...' : 'Connect to QuickBooks'}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Account Mappings */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Settings2 className="h-5 w-5" />
                Account Mappings
              </CardTitle>
              <CardDescription>
                Map H3Pay entities to QuickBooks accounts, customers, and items
              </CardDescription>
            </div>
            <Button onClick={openCreate} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Mapping
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {mappingsLoading ? (
            <p className="text-sm text-gray-500">Loading mappings...</p>
          ) : mappings.length === 0 ? (
            <p className="text-sm text-gray-500">
              No mappings configured. Default placeholder values will be used for QuickBooks sync.
            </p>
          ) : (
            <div className="space-y-6">
              {MAPPING_TYPE_GROUPS.map(({ group, types }) => {
                const groupMappings = mappings.filter((m) => types.includes(m.mapping_type));
                if (groupMappings.length === 0) return null;

                return (
                  <div key={group}>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">{group}</h3>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Type</TableHead>
                          <TableHead>Source</TableHead>
                          <TableHead>QB Ref Value</TableHead>
                          <TableHead>QB Ref Name</TableHead>
                          <TableHead className="w-24">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {groupMappings.map((m) => (
                          <TableRow key={m.id}>
                            <TableCell className="text-sm">{m.mapping_type_display}</TableCell>
                            <TableCell className="text-sm">
                              {m.source_name ?? (m.source_id != null ? `ID: ${m.source_id}` : '\u2014')}
                            </TableCell>
                            <TableCell className="font-mono text-sm">{m.qb_ref_value}</TableCell>
                            <TableCell className="text-sm">{m.qb_ref_name || '\u2014'}</TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button variant="ghost" size="icon" onClick={() => openEdit(m)}>
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDelete(m.id)}
                                  disabled={deleteMapping.isPending}
                                >
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sync Controls */}
      {status?.connected && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5" />
              Sync Data
            </CardTitle>
            <CardDescription>
              Push data from H3Pay to QuickBooks Online
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label>Sync Type</Label>
                <Select
                  value={syncType}
                  onValueChange={(v) => setSyncType(v as QuickBooksSyncPayload['sync_type'])}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="expenses">Expenses</SelectItem>
                    <SelectItem value="invoices">Invoices</SelectItem>
                    <SelectItem value="payroll">Payroll</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>From Date</Label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </div>
              <div>
                <Label>To Date</Label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button onClick={handleSync} disabled={sync.isPending}>
                <RefreshCw className={`h-4 w-4 mr-2 ${sync.isPending ? 'animate-spin' : ''}`} />
                {sync.isPending ? 'Syncing...' : 'Sync Now'}
              </Button>
              {syncResult && (
                <p className={`text-sm ${syncResult.startsWith('Error') ? 'text-red-600' : 'text-green-600'}`}>
                  {syncResult}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sync History */}
      {status?.connected && status.last_synced && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-500">
              Last successful sync: {formatDateTimeInTimeZone(status.last_synced, timeZone)}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Mapping Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingMapping ? 'Edit Mapping' : 'Add Mapping'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Mapping Type</Label>
              <Select
                value={form.mapping_type}
                onValueChange={(v) => setForm({ ...form, mapping_type: v as QBMappingType })}
                disabled={!!editingMapping}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MAPPING_TYPE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedTypeOption?.hasSource && (
              <div>
                <Label>Source ID</Label>
                <Input
                  type="number"
                  placeholder="e.g. ExpenseCategory ID, CostCode ID, or Project ID"
                  value={form.source_id}
                  onChange={(e) => setForm({ ...form, source_id: e.target.value })}
                />
                <p className="text-xs text-gray-500 mt-1">
                  The ID of the expense category, cost code, or project to map.
                </p>
              </div>
            )}

            <div>
              <Label>QB Reference Value *</Label>
              <Input
                placeholder="e.g. 42"
                value={form.qb_ref_value}
                onChange={(e) => setForm({ ...form, qb_ref_value: e.target.value })}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                The QuickBooks account/customer/item ID from your chart of accounts.
              </p>
            </div>

            <div>
              <Label>QB Display Name (optional)</Label>
              <Input
                placeholder="e.g. Office Supplies"
                value={form.qb_ref_name}
                onChange={(e) => setForm({ ...form, qb_ref_name: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={createMapping.isPending || updateMapping.isPending}
            >
              {createMapping.isPending || updateMapping.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
